import { useState } from 'react';

interface HeroImage {
  src: string;
  alt: string;
}

interface HeroCollageProps {
  images: HeroImage[];
}

export default function HeroCollage({ images }: HeroCollageProps) {
  const [current, setCurrent] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(0);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      setCurrent((prev) => (prev + 1) % images.length);
    } else if (distance < -minSwipeDistance) {
      setCurrent((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const main = images[0];
  const sub1 = images[1];
  const sub2 = images[2];
  const sub3 = images[3];
  const sub4 = images[4];

  return (
    <section className="w-full">
      {/* Mobile: carousel */}
      <div className="md:hidden">
        <div
          className="relative overflow-hidden aspect-[3/4]"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          {images.map((img, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-500 ${i === current ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
                loading={i === 0 ? 'eager' : 'lazy'}
              />
            </div>
          ))}

          {/* swipe hint */}
          <div className="absolute bottom-10 left-0 right-0 z-20 flex justify-center">
            <div className="bg-black/20 backdrop-blur-sm rounded-full px-3 py-1">
              <span className="text-[11px] text-white/90">
                {current + 1} / {images.length}
              </span>
            </div>
          </div>

          {/* dot indicator */}
          <div className="absolute bottom-3 left-0 right-0 z-20 flex justify-center gap-1.5">
            {images.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === current ? 'bg-white' : 'bg-white/40'}`}
                aria-label={`${i + 1}번 이미지`}
              />
            ))}
          </div>
        </div>

        {/* thumbnail preview */}
        <div className="flex gap-2 px-4 mt-2 overflow-x-auto pb-1 scrollbar-hide">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-colors ${i === current ? 'border-navy' : 'border-transparent'}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>

      {/* Desktop: editorial collage — left large + right 2×2 */}
      <div className="hidden md:flex gap-3 h-[62vh] max-h-[700px] w-full">
        {/* Left — large portrait */}
        <div className="flex-1 rounded-2xl overflow-hidden">
          <img
            src={main.src}
            alt={main.alt}
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>

        {/* Right — 2×2 grid */}
        <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-3">
          <div className="rounded-xl overflow-hidden">
            <img
              src={sub1.src}
              alt={sub1.alt}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src={sub2.src}
              alt={sub2.alt}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src={sub3.src}
              alt={sub3.alt}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src={sub4.src}
              alt={sub4.alt}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </div>
      </div>
    </section>
  );
}